import PptxGenJS, { Slide } from "pptxgenjs"
import {
	checkAnnuitySection,
	createAnnuitySection
} from "./annuity-generator"
import {
	checkCapitalReimbursementSection,
	createCapitalReimbursementSection
} from "./capital-reimbursement-generator"
import {
	checkInterestReimbursementSection,
	createInterestReimbursementSection
} from "./interest-reimbursement-generator"
import {
	checkRemainingCapitalSection,
	createRemainingCapitalSection
} from "./remaining-capital-generator"
import * as DebtUtils from "./utils"
import { ReportConfiguration } from "../../configuration"
import { MasterSlide } from "../pptx-configuration/master-slides"
import { Placeholder } from "../pptx-configuration/placeholders"
import { DocumentSection } from "../types"
import { formatTitle } from "../utils"
import * as LoanDomain from "../../../../domain/sfp/loan-domain"
import { LoanWithDecomposition, LocalAuthority, Ppi, Scenario } from "../../../../model"
import * as Constants from "../pptx-configuration/constants"

export function checkDebtSection(conf: ReportConfiguration): boolean {
	return (conf.debt.enableProspective || conf.debt.enableRetrospective)
	&& (checkAnnuitySection(conf)
		|| checkCapitalReimbursementSection(conf)
		|| checkInterestReimbursementSection(conf)
		|| checkRemainingCapitalSection(conf))
}

export function createDebtSection(
	pptx: PptxGenJS,
	configuration: ReportConfiguration,
	numbering: number[],
	decomposedLoans: LoanWithDecomposition[],
	scenario:Scenario,
	ppi:Ppi,
	loans:any,
	localAuthority:LocalAuthority
): DocumentSection {
	const hasAnnuitySection = checkAnnuitySection(configuration)
	const hasCapitalReimbursementSection = checkCapitalReimbursementSection(configuration)
	const hasInterestReimbursementSection = checkInterestReimbursementSection(configuration)
	const hasRemainingCapitalSection = checkRemainingCapitalSection(configuration)

	if (!(hasAnnuitySection
		|| hasCapitalReimbursementSection
		|| hasInterestReimbursementSection
		|| hasRemainingCapitalSection))
		return { slides: [], title: null }

	const title = formatTitle("Structuration de la dette", numbering)
	const slides: Slide[] = []
	const summarySlide = pptx.addNewSlide(MasterSlide.Default)
	summarySlide.addText(title, { placeholder: Placeholder.Title })
	slides.push(summarySlide)

	const subtitles: string[] = []
	let partNumber = 0

	if (!configuration.debt.enableSimulatedLoans) {
		decomposedLoans = LoanDomain
		.filterContractedLoansWithDecomposition(decomposedLoans)
	}

	const years = DebtUtils.createYears(configuration, decomposedLoans, localAuthority, ppi, scenario)

	if (hasAnnuitySection) {
		const annuitySection = createAnnuitySection(
			pptx,
			configuration,
			numbering.concat(++partNumber),
			decomposedLoans,
			years,
			localAuthority,
			loans,
			scenario,
			ppi
		)
		annuitySection.slides.forEach(s => slides.push(s))
		subtitles.push(annuitySection.title)
	}

	if (hasCapitalReimbursementSection) {
		const capitalReimbursementSection = createCapitalReimbursementSection(
			pptx,
			configuration,
			numbering.concat(++partNumber),
			decomposedLoans,
			years,
			localAuthority,
			loans,
			scenario,
			ppi
		)
		capitalReimbursementSection.slides.forEach(s => slides.push(s))
		subtitles.push(capitalReimbursementSection.title)
	}

	if (hasInterestReimbursementSection) {
		const interestReimbursementSection = createInterestReimbursementSection(
			pptx,
			configuration,
			numbering.concat(++partNumber),
			decomposedLoans,
			years,
			localAuthority,
			loans,
			scenario,
			ppi
		)
		interestReimbursementSection.slides.forEach(s => slides.push(s))
		subtitles.push(interestReimbursementSection.title)
	}

	if (hasRemainingCapitalSection) {
		const remainingCapitalSection = createRemainingCapitalSection(
			pptx,
			configuration,
			numbering.concat(++partNumber),
			decomposedLoans,
			years,
			scenario,
			ppi,
			loans,
			localAuthority,
			decomposedLoans.map(dl => dl.decomposition),
		)
		remainingCapitalSection.slides.forEach(s => slides.push(s))
		subtitles.push(remainingCapitalSection.title)
	}

	summarySlide.addText(
		subtitles.map(title => ({ text: title, options: { breakLine: true }})),
		Constants.getTextPlacementOptions
	)

	return { slides, title }
}



// WEBPACK FOOTER //
// ./src/component/management-report/generator/debt-generator/index.ts